import React from "react";
import {
  Box,
  Heading,
  Text,
  Grid,
  VStack,
  Badge,
  Progress,
  useColorModeValue,
} from "@chakra-ui/react";
import { QuestionSetPracticeAttemptScore } from "../../../Platform/Common/types";
import { fmtLocal } from "../../../utils/dates";
import { getResultPresentation } from "../../../utils/resultPresentation";

interface B2CQsetScoreViewerProps {
  attempted_question_set_score: QuestionSetPracticeAttemptScore;
}

const StatBlock = ({
  label,
  value,
  help,
}: {
  label: string;
  value: React.ReactNode;
  help?: React.ReactNode;
}) => (
  <Box>
    <Text fontSize="sm" color="gray.500">
      {label}
    </Text>
    <Text fontSize="2xl" fontWeight="bold">
      {value}
    </Text>
    {help && (
      <Text fontSize="sm" color="gray.500">
        {help}
      </Text>
    )}
  </Box>
);

const B2CQsetScoreViewer: React.FC<B2CQsetScoreViewerProps> = ({
  attempted_question_set_score,
}) => {
  const summaryBg = useColorModeValue("gray.50", "gray.700");
  const qs = attempted_question_set_score;
  const result = getResultPresentation(qs.has_passed, qs.percentage_obtained);

  return (
    <Box>
      <Box
        borderWidth="1px"
        borderRadius="2xl"
        boxShadow="md"
        bg={summaryBg}
        p={4}
      >
        <Heading size="lg" mb={2}>
          {qs.title}
        </Heading>

        <VStack align="stretch" spacing={0.5} mb={4}>
          <Text>Start: {fmtLocal(qs.student_start_time)}</Text>
          <Text>
            End: {qs.student_end_time ? fmtLocal(qs.student_end_time) : "N/A"}
          </Text>
        </VStack>

        <Grid templateColumns={["1fr", "repeat(3, 1fr)"]} gap={4}>
          <StatBlock
            label="Score"
            value={`${qs.total_score_obtained.toFixed(2)} / ${qs.max_qset_score}`}
            help="Obtained vs Max"
          />
          <StatBlock
            label="Percentage"
            value={`${qs.percentage_obtained.toFixed(2)}%`}
            help={`Target: ${qs.pass_percentage}%`}
          />
          <Box>
            <Text fontSize="sm" color="gray.500" mb={1}>
              Result
            </Text>
            <Badge colorScheme={result.colorScheme} fontSize="sm" px={2} py={0.5}>
              {result.label}
            </Badge>
          </Box>
        </Grid>

        <Box mt={4}>
          <Progress
            value={qs.percentage_obtained}
            size="sm"
            borderRadius="full"
            colorScheme={result.colorScheme}
          />
          <Text mt={2} fontSize="sm" color="gray.600">
            {result.message}
          </Text>
        </Box>

        {qs.remarks && (
          <Text mt={4} fontStyle="italic" color="gray.500">
            Remarks: {qs.remarks}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default B2CQsetScoreViewer;